<script setup lang="ts">
import { computed, ref } from 'vue';
import { UiIcon, UiImg } from '@shared/ui';
import type { UiAvatarProps } from '@shared/ui';
import { NuxtLink } from '#components';

const {
  tag = 'span',
  icon,
  text,
  href,
  target = '_self',
  rel,
  src,
  alt = '',
  size = 'md',
  type = 'round',
} = defineProps<UiAvatarProps>();

const emit = defineEmits<{
  (e: 'error', event: Event): void;
}>();

const hasLoadError = ref(false);

const getTagName = computed<string | typeof NuxtLink>(() => (tag === 'a' ? NuxtLink : (tag as string)));

const getRel = computed<string | null>(() => rel ?? null);

const getImgSize = computed<number>(() => {
  switch (size) {
    case 'xs': return 18;
    case 'sm': return 20;
    case 'md': return 30;
    case 'lg': return 38;
    case 'xl': return 46;
    case 'xxl': return 62;
    case 'xxxl': return 94;
    default: return 30;
  }
});

const handleError = (e: Event) => {
  hasLoadError.value = true;
  emit('error', e);
};

// Карта классов для обёртки: размеры (w/h) и толщина бордера
const wrapperSizeMap: Record<NonNullable<UiAvatarProps['size']>, string> = {
  xs: 'w-5 h-5 border',
  sm: 'w-6 h-6 border',
  md: 'w-8 h-8 border',
  lg: 'w-10 h-10 border',
  xl: 'w-12 h-12 border-2',
  xxl: 'w-16 h-16 border-2',
  xxxl: 'w-24 h-24 border-2',
};

// Карта для размеров иконки
const iconSizeMap: Record<NonNullable<UiAvatarProps['size']>, string> = {
  xs: 'w-3.5 h-3.5', // 0.875rem
  sm: 'w-[1.375rem] h-[1.375rem]',
  md: 'w-[1.875rem] h-[1.875rem]',
  lg: 'w-[2.375rem] h-[2.375rem]',
  xl: 'w-[2.875rem] h-[2.875rem]',
  xxl: 'w-[3.875rem] h-[3.875rem]',
  xxxl: 'w-[5.875rem] h-[5.875rem]',
};

// Карта для размера текста внутри
const textSizeMap: Record<NonNullable<UiAvatarProps['size']>, string> = {
  xs: 'text-xs',
  sm: 'text-[12px]',
  md: 'text-[14px]',
  lg: 'text-[16px]',
  xl: 'text-[18px]',
  xxl: 'text-[20px]',
  xxxl: 'text-[20px]',
};

const wrapperClasses = computed(() => {
  const base = [
    'inline-flex',
    'items-center',
    'justify-center',
    'flex-shrink-0',
    'overflow-hidden',
    'leading-none',
    'transition',
  ];

  // цвета и фон через css-переменные
  const colors = [
    'border-white',
    'bg-gray-50',
    'text-gray-900',
  ];

  // rounded
  const shape = type === 'round' ? 'rounded-full' : 'rounded-[var(--unit)]';

  // action (ссылки/кнопки) — pointer + interactive states
  const action = ['a', 'button'].includes(String(tag))
    ? [
        'cursor-pointer',
        'focus-visible:outline-2',
        'focus-visible:outline-offset-1',
        'focus-visible:outline-blue-600',
        'hover:border-blue-700',
        'hover:text-blue-700',
        'active:border-blue-600',
        'active:text-blue-600',
      ]
    : [];

  const sizeCls = wrapperSizeMap[size ?? 'md'];

  return [...base, ...colors, shape, sizeCls, ...action].join(' ');
});

const imgClass = computed(() => {
  return 'object-cover w-full h-full';
});

const iconClass = computed(() => {
  const sizeCls = iconSizeMap[size ?? 'md'];
  return sizeCls;
});

const textClass = computed(() => {
  const sizeCls = textSizeMap[size ?? 'md'];
  return `font-bold ${sizeCls}`;
});
</script>

<template>
  <component
    :is="getTagName"
    :href="href"
    :target="tag === 'a' ? target : null"
    :rel="getRel"
    :class="wrapperClasses"
  >
    <UiImg
      v-if="src && !hasLoadError"
      tag="span"
      :src="src"
      :alt="alt"
      :width="getImgSize"
      :class="imgClass"
      @error="handleError"
    />

    <UiIcon
      v-else-if="icon"
      :name="icon"
      :class="iconClass"
    />

    <span
      v-else-if="text"
      :class="textClass"
    >{{ text }}</span>

    <span
      v-if="alt"
      class="sr-only"
    >{{ alt }}</span>
  </component>
</template>
